import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/api';

const AreaList = () => {
  const [areas, setAreas] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    department_id: '',
    company_id: '',
    created_by: 1
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [areasResponse, departmentsResponse, companiesResponse] = await Promise.all([
        apiService.areas.getAll(),
        apiService.departments.getAll(),
        apiService.companies.getAll()
      ]);
      setAreas(areasResponse.data);
      setDepartments(departmentsResponse.data);
      setCompanies(companiesResponse.data);
      // Auto-select first company in form data if available
      if (companiesResponse.data.length > 0 && !formData.company_id) {
        setFormData(prev => ({ ...prev, company_id: companiesResponse.data[0].id.toString() }));
      }
    } catch (err) {
      setError('Failed to load data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        ...formData,
        department_id: parseInt(formData.department_id),
        company_id: parseInt(formData.company_id)
      };

      if (editingArea) {
        await apiService.areas.update(editingArea.id, {
          ...dataToSubmit,
          updated_by: 1
        });
      } else {
        await apiService.areas.create(dataToSubmit);
      }
      
      setShowForm(false);
      setEditingArea(null);
      // Auto-select first company if available
      const firstCompanyId = companies.length > 0 ? companies[0].id.toString() : '';
      setFormData({ name: '', department_id: '', company_id: firstCompanyId, created_by: 1 });
      fetchData();
    } catch (err) {
      setError('Failed to save area');
      console.error('Error saving area:', err);
    }
  };

  const handleEdit = (area) => {
    setEditingArea(area);
    setFormData({
      name: area.name,
      department_id: area.department_id.toString(),
      company_id: area.company_id.toString(),
      created_by: area.created_by || 1
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this area?')) {
      try {
        await apiService.areas.delete(id);
        fetchData();
      } catch (err) {
        setError('Failed to delete area');
        console.error('Error deleting area:', err);
      }
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingArea(null);
    // Auto-select first company if available
    const firstCompanyId = companies.length > 0 ? companies[0].id.toString() : '';
    setFormData({ name: '', department_id: '', company_id: firstCompanyId, created_by: 1 });
  };

  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept ? dept.name : 'Unknown';
  };

  const getCompanyName = (compId) => {
    const comp = companies.find(c => c.id === compId);
    return comp ? comp.name : 'Unknown';
  };

  if (loading) return <div className="loading">Loading areas...</div>;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Areas</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          Add Area
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {showForm && (
        <div className="form">
          <h3>{editingArea ? 'Edit Area' : 'Add New Area'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Area Name *</label>
              <input
                type="text"
                className="form-control"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Department *</label>
              <select
                className="form-control"
                value={formData.department_id}
                onChange={(e) => setFormData({...formData, department_id: e.target.value})}
                required
              >
                <option value="">Select a department</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Company *</label>
              <select
                className="form-control"
                value={formData.company_id}
                onChange={(e) => setFormData({...formData, company_id: e.target.value})}
                required
              >
                {companies.length === 0 ? (
                  <option value="">No companies available</option>
                ) : (
                  companies.map(comp => (
                    <option key={comp.id} value={comp.id}>
                      {comp.name}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div style={{display: 'flex', gap: '1rem'}}>
              <button type="submit" className="btn btn-primary">
                {editingArea ? 'Update' : 'Create'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Department</th>
              <th>Company</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {areas.map(area => (
              <tr key={area.id}>
                <td>{area.id}</td>
                <td>
                  <Link 
                    to={`/areas/${area.id}`}
                    style={{ 
                      color: '#034955', 
                      textDecoration: 'none',
                      fontWeight: 500
                    }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                  >
                    {area.name}
                  </Link>
                </td>
                <td>{getDepartmentName(area.department_id)}</td>
                <td>{getCompanyName(area.company_id)}</td>
                <td>{new Date(area.created_at).toLocaleDateString()}</td>
                <td>
                  <Link 
                    to={`/areas/${area.id}`}
                    className="btn btn-primary btn-small"
                    style={{marginRight: '0.5rem'}}
                  >
                    Details
                  </Link>
                  <button 
                    className="btn btn-secondary btn-small" 
                    onClick={() => handleEdit(area)}
                    style={{marginRight: '0.5rem'}}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-danger btn-small" 
                    onClick={() => handleDelete(area.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {areas.length === 0 && (
          <div style={{padding: '2rem', textAlign: 'center', color: '#6c757d'}}>
            No areas found. Add one to get started!
          </div>
        )}
      </div>
    </div>
  );
};

export default AreaList;